import type { FC } from 'react';
import { useCallback } from 'react';
import { useAppTranslation } from '@flowus/common/i18n/use-translation';
import { Button } from 'src/common/components/button';
import { useOpenModal } from 'src/common/components/next-modal';
import { getActivity } from 'src/hooks/activities/use-activity';
import { OpenSettingFrom, SettingMenuType } from 'src/views/main/setting-modal/type';
import { useOpenSettingModal } from 'src/views/main/setting-modal/use-open-setting-modal';
import { createActivityModalAnchor } from '../activities/create-activity-modal-anchor';

export enum TaskProgressType {
  FINISH_BASIC_TASK = 'FINISH_BASIC_TASK',
  NOTIFICATION = 'NOTIFICATION',
}

interface Props {
  onCloseModal: () => void;
  type: TaskProgressType;
}

export const TaskProgress: FC<Props> = ({ type, onCloseModal }) => {
  const { t } = useAppTranslation();
  const openSettingModal = useOpenSettingModal();

  const handleClick = () => {
    onCloseModal();
    openSettingModal({
      defaultMenuType: SettingMenuType.integral,
      from: OpenSettingFrom.new_user_task,
    });
  };
  return (
    <div className="next-modal w-[332px] rounded p-[30px]">
      <div className="text-t1-medium">
        {type === TaskProgressType.FINISH_BASIC_TASK && (
          <>
            {t('components.new_user_task.task_progress.finish_basic_task_message')}
            <span className="text-active_color">
              {t('components.new_user_task.task_progress.advanced_task')}
            </span>
          </>
        )}
        {type === TaskProgressType.NOTIFICATION && (
          <>{t('components.new_user_task.task_progress.notification_message')}</>
        )}
      </div>
      <div className="flex items-center mt-[30px]">
        <Button colorType="black" onClick={onCloseModal} className="border-grey5">
          {t('common.self_explore')}
        </Button>
        <Button colorType="active" className="ml-2.5" onClick={handleClick}>
          {t('common.go_now')}
        </Button>
      </div>
    </div>
  );
};

export const useOpenOneTaskModal = () => {
  const { t } = useAppTranslation();
  const openModal = useOpenModal();
  const openSettingModal = useOpenSettingModal();

  return useCallback(
    (taskId: string) => {
      return new Promise((resolve) => {
        const activity = getActivity(taskId);
        openModal.modal({
          closeAfterCallBack: () => {
            resolve(0);
          },
          content: ({ onCloseModal }) => {
            return (
              <div className="next-modal w-[400px] p-[30px] flex flex-col">
                <p className="text-center text-[70px] leading-[70px]">😀</p>
                <p className="text-t1-medium mt-2.5 text-center">
                  {t('components.new_user_task.task_progress.congratulations')}
                </p>
                <p className="text-t2 text-center mt-2.5">
                  {t('components.new_user_task.task_progress.first_task_completed', {
                    score: activity?.score,
                  })}{' '}
                  <br />
                  {t('components.new_user_task.task_progress.points_redeemable')}
                </p>
                <Button
                  colorType="active"
                  className="mt-10 flex-1 p-1"
                  onClick={() => {
                    onCloseModal();
                    resolve(0);
                    openSettingModal({
                      defaultMenuType: SettingMenuType.integral,
                      from: OpenSettingFrom.new_user_task,
                    });
                  }}
                >
                  {t('components.new_user_task.task_progress.unlock_more')}
                </Button>
              </div>
            );
          },
        });
      }).catch((err) => {
        console.warn(err);
      });
    },
    [openModal, openSettingModal, t]
  );
};

export const useOpenTaskProgress = () => {
  const openModal = useOpenModal();

  return useCallback(
    (type: TaskProgressType) => {
      const activityModalAnchor = createActivityModalAnchor();

      openModal.dropdown({
        // autoClose: false,
        popcorn: activityModalAnchor.popcorn,
        placement: 'top-start',
        offset: [-50, 0],
        closeAfterCallBack: activityModalAnchor.remove,
        content: ({ onCloseModal }) => {
          return <TaskProgress type={type} onCloseModal={onCloseModal} />;
        },
      });
    },
    [openModal]
  );
};
